import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import AddressPanel from 'components/AddressPanel/AddressPanel';
import Button from 'components/Button/Button';
import DataSetsPanel from 'components/DataSetsPanel/DataSetsPanel';
import DetailSection from 'components/DetailSection/DetailSection';
import Loader from 'components/Loader/Loader';
import Modal from 'components/Modal/Modal';
import ModalMockFillButton from 'components/ModalMockFillButton/ModalMockFillButton';
import ProfitCenterLinksCustomers from 'components/ProfitCenterLinksCustomers/ProfitCenterLinksCustomers';
import { DATA_SETS_ENUM_TYPES, UID_NAME_TUPLES } from 'constants/dataSets';
import {
  NACE_CODE, NACE_SECTION, VALUE_CHAIN_LIST, VALUE_CHAIN_SECTION,
} from 'constants/formFields';
import { BUTTON_VARIANT, VALUE_CHAIN_UNIT_TYPES } from 'constants/interfaces';
import { useAiSubScreenContext } from 'contexts/AiScreenContext';
import useDataSetsDate from 'hooks/useDataSetsDate';
import useFieldsErrors from 'hooks/useFieldsErrors';
import useGeoLocations from 'hooks/useGeoLocations';
import useScrollToError from 'hooks/useScrollToError';
import useValueChainColumns from 'hooks/useValueChainColumns';
import useValueChainPosition from 'hooks/useValueChainPosition';
import { getCustomerModalMockValues } from 'mocks/companyMapModalMocks';
import GeneralCustomerInfo from 'pages/UserPages/CreateCustomer/components/GeneralCustomerInfo';
import { useAppDispatch } from 'store/hooks/useApp';
import { useEnumQuery, useGetNaceCodeTreeQuery } from 'store/slices/dataSets/apis/dataSetsApi';
import { selectEnums, selectNaceCodes } from 'store/slices/dataSets/selectors';
import { setDataSetsState } from 'store/slices/dataSets/slice';
import { useUserMeQuery } from 'store/slices/user/apis/user';
import {
  useCreateCustomerMutation, useGetBoardColumnNamesQuery, useLazyGetValueChainMapBoardQuery,
  useLazyGetValueChainMapBusinessModelQuery, useUpdateCustomerMutation,
} from 'store/slices/valueChainMap/apis/valueChainMapApi';
import getRequestDataForCustomerUpdate from 'utils/formHelpers/getRequestDataForCustomerUpdate';
import { extractApiErrorMessage } from 'utils/helpers';

import useCreateCustomerForm from './hooks/useCreateCustomerForm';

import type { TDate } from 'components/DatePicker/interfaces/TDate';
import type IOption from 'components/MappingModal/interfaces/IOption';
import type { Path, PathValue } from 'react-hook-form';
import type ICustomer from 'store/slices/valueChainMap/interfaces/ICustomer';
import type { TCreateCustomerSchema } from 'utils/validators';
import type ICustomerModal from './interfaces/ICustomerModal';

import styles from './scss/CustomerModal.module.scss';

function CustomerModal({
  closeProfitCenterModal,
  valueChainMapId,
  setSelectedCustomer,
  currentTargetSwimlaneId,
  selectedCustomer,
  valueChainForm,
}: ICustomerModal) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { setFieldsErrors } = useFieldsErrors();
  const placeholder = selectedCustomer?.id ? t('common.notProvided') : '';
  const modalRef = useRef<HTMLDivElement>(null);

  const [createCustomer, { isLoading: isCreateCustomerLoading }] = useCreateCustomerMutation();
  const [updateCustomer, { isLoading: isUpdateCustomerLoading }] = useUpdateCustomerMutation();
  const [fetchVCBusinessModel] = useLazyGetValueChainMapBusinessModelQuery();
  const [fetchVCBoard] = useLazyGetValueChainMapBoardQuery();

  const generateTitle = () => (selectedCustomer?.id
    ? t('customers.editCustomer')
    : t('customers.addNewCustomer'));

  // Open over the map: the assistant is told which unit is being edited, or that a new one is.
  useAiSubScreenContext({
    name: generateTitle(),
    customData: {
      unitType: VALUE_CHAIN_UNIT_TYPES.CUSTOMER,
      id: selectedCustomer?.id,
      name: selectedCustomer?.name,
    },
  });

  const { data: userData } = useUserMeQuery();
  const companyCurrency = userData?.data?.company?.currency;

  const {
    data: boardColumnNamesData,
    isLoading: isBoardColumnNamesLoading,
  } = useGetBoardColumnNamesQuery({ valueChainMapId });

  const handleGetCurrentColumn = (id?: number) => {
    let currentColumn: { id: number, name: string } = { id: 0, name: '' };
    if (id) {
      currentColumn = {
        id,
        name: boardColumnNamesData?.data?.find(
          (column) => column?.columnId === id,
        )?.columnName.split('. ')[1]
          || '',
      };
    }
    return currentColumn;
  };

  const { data: enumData, isLoading: isEnumLoading } = useEnumQuery({
    types: [DATA_SETS_ENUM_TYPES.currency, DATA_SETS_ENUM_TYPES.ownership, DATA_SETS_ENUM_TYPES.valueChainSection,
      DATA_SETS_ENUM_TYPES.businessUnitType, DATA_SETS_ENUM_TYPES.assetCategory, DATA_SETS_ENUM_TYPES.ownedLeased,
      DATA_SETS_ENUM_TYPES.upstreamTier, DATA_SETS_ENUM_TYPES.priorityRating],
  }, {
    selectFromResult: (result) => ({ ...result, data: selectEnums(result.data) }),
  });

  const { data: naceCodesData, isLoading: isNaceCodesTreeLoading } = useGetNaceCodeTreeQuery(undefined, {
    selectFromResult: (result) => ({ ...result, data: selectNaceCodes(result.data) }),
  });

  const isPageLoading = isEnumLoading || isNaceCodesTreeLoading || isCreateCustomerLoading || isUpdateCustomerLoading
    || isBoardColumnNamesLoading;

  const {
    control,
    handleSubmit,
    reset,
    setError,
    fieldsName,
    watch,
    setValue,
    profitCenterIds,
    formState,
    isUniqueIdentifierEditable,
  } = useCreateCustomerForm({
    recordId: selectedCustomer?.id ? String(selectedCustomer.id) : '',
    isPageLoading,
    valueChainMapId,
    valueChainSectionCurrent: handleGetCurrentColumn(currentTargetSwimlaneId)?.name,
  });

  const {
    handleGetVcOptions,
    vcOptionsList,
    listPosition,
  } = useValueChainPosition(watch(VALUE_CHAIN_LIST)?.value, valueChainMapId);

  const { dateOpened, onDateOpenedChange } = useDataSetsDate<TCreateCustomerSchema>({ watch, setValue });

  const onChangeDate = (date: TDate, onChange: (date: TDate) => void) => {
    onChange(date);
    onDateOpenedChange(date);
  };

  const naceDivisions = watch(NACE_SECTION)?.divisions || [];

  const onNaceSelectionChange = () => {
    const emptyOption = null as PathValue<TCreateCustomerSchema, Path<TCreateCustomerSchema>>;
    setValue(NACE_CODE, emptyOption);
  };

  const {
    onFindCountries, onFindStates, onFindCities, isStatesDisabled, onChangeCountry, onChangeState,
    isCitiesDisabled, defaultCountries,
  } = useGeoLocations<TCreateCustomerSchema>({ watch, setValue });
  const profitCenterOptions = (
    profitCenterIds as Record<string, IOption[]> | undefined
  )?.[UID_NAME_TUPLES.profitCenter];

  const { getColumnId } = useValueChainColumns({
    enumData,
    boardColumnNamesData,
  });

  const { scrollToErrors } = useScrollToError({
    formState,
    scrollContainerRef: modalRef,
    scrollContainerSelector: '[class*="OwnOperationModalFormWrapper"]',
  });

  const isMockFillVisible = !selectedCustomer?.id && process.env.REACT_APP_API?.includes('api.dev');

  const onFillMockData = () => {
    reset({
      ...watch(),
      ...getCustomerModalMockValues({
        enumData,
        naceCodesData,
        profitCenterOptions,
        countryOptions: defaultCountries,
      }),
    });
  };

  const onCreateCustomerSubmit = handleSubmit((values) => {
    if (selectedCustomer?.id) {
      updateCustomer({
        data: getRequestDataForCustomerUpdate({
          values,
          recordId: `${selectedCustomer.id}`,
          companyCurrency,
          valueChainForm,
          includeUniqueIdentifier: isUniqueIdentifierEditable,
        }),
        valueChainMapId,
        columnId: getColumnId(`${watch(VALUE_CHAIN_LIST)?.label}. ${watch(VALUE_CHAIN_SECTION)?.label}`)?.toString(),
      })
        .unwrap()
        .then(() => {
          if (valueChainMapId) {
            setTimeout(() => {
              fetchVCBusinessModel({ valueChainMapId });
              fetchVCBoard({ valueChainMapId });
            }, 10000);
          }
          closeProfitCenterModal();
        })
        .catch((errors) => {
          const hasFieldErrors = setFieldsErrors({ errors, setError, fieldsName });
          if (hasFieldErrors) {
            scrollToErrors();
          } else {
            toast.error(extractApiErrorMessage(errors) || t('common.unknownError'));
          }
        });
    } else {
      createCustomer({
        data: getRequestDataForCustomerUpdate({ values, companyCurrency, valueChainForm }),
        valueChainMapId,
        columnId: getColumnId(`${watch(VALUE_CHAIN_LIST)?.label}. ${watch(VALUE_CHAIN_SECTION)?.label}`)?.toString()
        || currentTargetSwimlaneId?.toString(),
      })
        .unwrap()
        .then(({ data }) => {
          setSelectedCustomer({ id: data.id, uniqueIdentifier: data.uniqueIdentifier } as ICustomer);
          dispatch(setDataSetsState({
            selectedCustomer: { label: data.uniqueIdentifier, value: data.uniqueIdentifier },
          }));
          if (valueChainMapId) {
            setTimeout(() => {
              fetchVCBusinessModel({ valueChainMapId });
              fetchVCBoard({ valueChainMapId });
            }, 10000);
          }
          closeProfitCenterModal();
        })
        .catch((errors) => {
          const hasFieldErrors = setFieldsErrors({ errors, setError, fieldsName });
          if (hasFieldErrors) {
            scrollToErrors();
          } else {
            toast.error(extractApiErrorMessage(errors) || t('common.unknownError'));
          }
        });
    }
  });

  return (
    <Modal
      open
      isButtonsVisible={false}
      className={styles.modal}
      title={generateTitle()}
      titleAction={isMockFillVisible ? (
        <ModalMockFillButton
          onClick={onFillMockData}
          disabled={isPageLoading}
        />
      ) : null}
    >
      <div ref={modalRef}>
        <div>
          <div className={styles.OwnOperationModalFormWrapper}>
            <Loader isPageLoading={isPageLoading} isFullLoader={false} />
            <GeneralCustomerInfo
              control={control}
              enumData={enumData}
              placeholder={placeholder}
              recordId={selectedCustomer?.id ? String(selectedCustomer.id) : ''}
              isUniqueIdentifierEditable={isUniqueIdentifierEditable}
              dateOpened={dateOpened}
              onChangeDate={onChangeDate}
              isPageLoading={isPageLoading}
              handleGetVcOptions={handleGetVcOptions}
              vcOptionsList={vcOptionsList}
              listPosition={listPosition}
              setValue={setValue}
            />
            <DetailSection
              control={control}
              naceCodesData={naceCodesData}
              naceDivisions={naceDivisions}
              onNaceSelectionChange={onNaceSelectionChange}
              placeholder={placeholder}
              recordId={selectedCustomer?.id ? String(selectedCustomer.id) : ''}
            />
            <DataSetsPanel title={t('customers.selections.financials')} className={styles.fullWidth}>
              <ProfitCenterLinksCustomers
                control={control}
                profitCenterIds={profitCenterIds}
                placeholder={placeholder}
                watch={watch}
                setValue={setValue}
              />
            </DataSetsPanel>
            <AddressPanel
              control={control}
              placeholder={placeholder}
              onFindCountries={onFindCountries}
              onFindStates={onFindStates}
              onFindCities={onFindCities}
              isStatesDisabled={isStatesDisabled}
              isCitiesDisabled={isCitiesDisabled}
              onChangeCountry={onChangeCountry}
              onChangeState={onChangeState}
              menuPosition="absolute"
              defaultCountries={defaultCountries}
            />
          </div>
        </div>
        <div className={styles.buttons}>
          <Button
            title={t('common.cancel')}
            labelKey="common.cancel"
            variant={BUTTON_VARIANT.cancel}
            onClick={closeProfitCenterModal}
          />
          <div className={styles.buttonsRight}>
            {selectedCustomer?.id ? (
              <Button
                title={t('common.save')}
                labelKey="common.save"
                onClick={onCreateCustomerSubmit}
                isLoading={isPageLoading}
              />
            ) : null}
            {!selectedCustomer?.id ? (
              <Button
                title={t('common.create')}
                labelKey="common.create"
                onClick={onCreateCustomerSubmit}
                isLoading={isPageLoading}
              />
            ) : null}
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default CustomerModal;
